import apiClient from './client';
import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { PagedResponse, SearchParams } from './types';

// ── File Document ──

export interface FileDocument {
  id: string;
  tenant_id: string;
  name: string;
  original_name: string;
  file_path: string;
  file_size: number;
  content_type: string;
  storage_type: string;
  entity_type: string;
  entity_id: string;
  category: string;
  description: string;
  is_public: boolean;
  download_count: number;
  checksum: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface CreateFileRequest {
  name: string;
  original_name: string;
  file_size: number;
  content_type: string;
  entity_type?: string;
  entity_id?: string;
  category?: string;
  description?: string;
  is_public?: boolean;
  tags?: string[];
}

export interface UpdateFileRequest {
  name?: string;
  entity_type?: string;
  entity_id?: string;
  category?: string;
  description?: string;
  is_public?: boolean;
  tags?: string[];
}

export interface FileFilters extends SearchParams {
  entity_type?: string;
  entity_id?: string;
  category?: string;
}

// ── File Share Document ──

export interface FileShareDocument {
  id: string;
  tenant_id: string;
  file_id: string;
  shared_with_user_id: string;
  shared_with_email: string;
  permission: string;
  expires_at: string;
  access_count: number;
  last_accessed_at: string;
  is_active: boolean;
  share_token: string;
  notes: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface CreateFileShareRequest {
  file_id: string;
  shared_with_user_id?: string;
  shared_with_email?: string;
  permission: string;
  expires_at?: string;
  notes?: string;
}

export interface UpdateFileShareRequest {
  permission?: string;
  expires_at?: string;
  is_active?: boolean;
  notes?: string;
}

export interface FileShareFilters extends SearchParams {
  file_id?: string;
  shared_with_user_id?: string;
}

// ── Constants ──

export const FILE_STORAGE_TYPES = ['local', 'cloud'] as const;

export const FILE_CATEGORIES = [
  'document',
  'image',
  'spreadsheet',
  'presentation',
  'archive',
  'other',
] as const;

export const FILE_ENTITY_TYPES = [
  'customer',
  'contact',
  'opportunity',
  'order',
  'quote',
  'ticket',
] as const;

export const FILE_SHARE_PERMISSIONS = ['view', 'download'] as const;

// ── File API ──

export const getFiles = (params: FileFilters = {}) =>
  apiGet<PagedResponse<FileDocument>>('/api/v1/files', params as Record<string, unknown>);

export const getFile = (id: string) =>
  apiGet<FileDocument>(`/api/v1/files/${id}`);

export const createFile = (data: CreateFileRequest) =>
  apiPost<FileDocument>('/api/v1/files', data);

export const updateFile = (id: string, data: UpdateFileRequest) =>
  apiPut<FileDocument>(`/api/v1/files/${id}`, data);

export const deleteFile = (id: string) =>
  apiDelete(`/api/v1/files/${id}`);

export const uploadFile = (id: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiPost<FileDocument>(`/api/v1/files/${id}/upload`, formData);
};

export const downloadFile = async (id: string) => {
  const response = await apiClient.get(`/api/v1/files/${id}/download`, {
    responseType: 'blob',
  });
  return { data: response.data as Blob };
};

export const getFilesByEntity = (entityType: string, entityId: string, params?: SearchParams) =>
  apiGet<PagedResponse<FileDocument>>(
    `/api/v1/files/by-entity/${entityType}/${entityId}`,
    params as Record<string, unknown>
  );

// ── File Share API ──

export const getFileShares = (params: FileShareFilters = {}) =>
  apiGet<PagedResponse<FileShareDocument>>('/api/v1/file-shares', params as Record<string, unknown>);

export const getFileShare = (id: string) =>
  apiGet<FileShareDocument>(`/api/v1/file-shares/${id}`);

export const createFileShare = (data: CreateFileShareRequest) =>
  apiPost<FileShareDocument>('/api/v1/file-shares', data);

export const updateFileShare = (id: string, data: UpdateFileShareRequest) =>
  apiPut<FileShareDocument>(`/api/v1/file-shares/${id}`, data);

export const deleteFileShare = (id: string) =>
  apiDelete(`/api/v1/file-shares/${id}`);

export const getSharesByFile = (fileId: string, params?: SearchParams) =>
  apiGet<PagedResponse<FileShareDocument>>(
    `/api/v1/file-shares/by-file/${fileId}`,
    params as Record<string, unknown>
  );
